import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";

// ── Cinematic horizontal mosaic — same avatar pool as CinematicLoadingScreen ──
const MOSAIC_AVATARS = [
  "/24-girl.png", "/27-boy.png", "/25-girl.png", "/28-boy.png",
  "/34-girl.png", "/29-boy.png", "/36-girl.png", "/30-boy.png",
  "/38-girl.png", "/31-boy.png", "/35-girl.png", "/32-boy.png",
  "/26-girl.png", "/33-boy.png", "/37-boy.png",
];

// ── Verdict config ─────────────────────────────────────────────────────────────
const VERDICT = {
  shortlisted: { color: "#22c55e", bg: "rgba(34,197,94,0.12)",  border: "rgba(34,197,94,0.30)",  label: "Shortlisted ✅", glow: "rgba(34,197,94,0.30)" },
  conditional: { color: "#fbbf24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.30)", label: "Conditional ⚠️", glow: "rgba(251,191,36,0.30)" },
  rejected:    { color: "#e63946", bg: "rgba(230,57,70,0.12)",  border: "rgba(230,57,70,0.30)",  label: "Not Selected ❌", glow: "rgba(230,57,70,0.30)" },
  pending:     { color: "#60a5fa", bg: "rgba(96,165,250,0.12)", border: "rgba(96,165,250,0.30)", label: "Under Review 🔍", glow: "rgba(96,165,250,0.30)" },
};

function ScoreRing({ score, max }) {
  const pct   = max > 0 ? score / max : 0;
  const r     = 28;
  const circ  = 2 * Math.PI * r;
  const dash  = pct * circ;
  const color = pct >= 0.7 ? "#22c55e" : pct >= 0.5 ? "#fbbf24" : "#e63946";
  return (
    <svg width={70} height={70} style={{ flexShrink: 0 }}>
      <circle cx={35} cy={35} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth={6} />
      <circle cx={35} cy={35} r={r} fill="none"
        stroke={color} strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform="rotate(-90 35 35)"
        style={{ filter: `drop-shadow(0 0 5px ${color})` }}
      />
      <text x={35} y={32} textAnchor="middle" fill="#f1f1f1" fontSize={13} fontWeight={900} fontFamily="monospace">{score}</text>
      <text x={35} y={44} textAnchor="middle" fill="rgba(255,255,255,0.35)" fontSize={9}>/{max}</text>
    </svg>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "Asia/Kolkata",
  }) + " IST";
}

function posLabel(id) {
  return (id || "—").replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── Video player with error fallback ──────────────────────────────────────────
function VideoPlayer({ url }) {
  const [videoError, setVideoError] = useState(false);

  if (videoError) {
    return (
      <div style={{
        padding: "2.5rem 2rem", textAlign: "center",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
      }}>
        <div style={{ fontSize: 40 }}>⚠️</div>
        <div style={{ color: "#fbbf24", fontWeight: 700, fontSize: 15 }}>
          Browser couldn't play the video inline
        </div>
        <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 12, maxWidth: 420, lineHeight: 1.6 }}>
          Recording file not found in storage. This session was recorded before the upload fix — please take a new session to get a fresh recording.
        </div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            background: "linear-gradient(135deg,#e63946,#c1121f)",
            color: "#fff", borderRadius: 9,
            padding: "9px 28px", fontWeight: 700, fontSize: 13,
            textDecoration: "none", marginTop: 4,
          }}
        >
          ↗ Open Recording in New Tab
        </a>
      </div>
    );
  }

  return (
    <>
      <div style={{ background: "#000", lineHeight: 0 }}>
        {/* No crossOrigin attribute — presigned S3 URLs work as plain media loads.
            Adding crossOrigin triggers CORS mode which requires S3 to expose
            Content-Range/Accept-Ranges headers; without those Chrome blocks the stream. */}
        <video
          src={url}
          controls
          autoPlay
          preload="auto"
          style={{ width: "100%", maxHeight: "75vh", display: "block" }}
          onError={() => setVideoError(true)}
        />
      </div>
      <div style={{
        padding: "8px 20px", textAlign: "center",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderTop: "1px solid rgba(255,255,255,0.05)",
      }}>
        <span style={{ color: "rgba(255,255,255,0.22)", fontSize: 11, fontFamily: "monospace" }}>
          Press ESC or click outside to close
        </span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "rgba(99,165,250,0.7)", fontSize: 11, textDecoration: "none",
            fontFamily: "monospace",
          }}
        >
          ↗ Open in new tab
        </a>
      </div>
    </>
  );
}

// ── Recording modal ────────────────────────────────────────────────────────────
function RecordingModal({ sessionTitle, url, error, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.9)",
        backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: "modal-in 0.22s ease-out",
      }}
    >
      <style>{`
        @keyframes modal-in { from{opacity:0;transform:scale(0.94)} to{opacity:1;transform:scale(1)} }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(900px, 94vw)",
          background: "rgba(6,10,31,0.98)",
          border: "1px solid rgba(230,57,70,0.4)",
          borderRadius: 18,
          boxShadow: "0 0 80px rgba(230,57,70,0.2), 0 30px 100px rgba(0,0,0,0.9)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 22px",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(230,57,70,0.07)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              background: error ? "#fbbf24" : "#e63946",
              boxShadow: `0 0 8px ${error ? "#fbbf24" : "#e63946"}`,
            }} />
            <span style={{
              color: "#f1f1f1", fontWeight: 800, fontSize: 13,
              fontFamily: "'Barlow Condensed','Impact',sans-serif",
              letterSpacing: "0.06em", textTransform: "uppercase",
            }}>
              {error ? "⚠ Recording" : "▶ Session Recording"} — {sessionTitle}
            </span>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.6)", borderRadius: 6,
            width: 30, height: 30, fontSize: 15, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700,
          }}>✕</button>
        </div>

        {/* Content */}
        {error ? (
          <div style={{
            padding: "3rem 2rem", textAlign: "center",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 16,
          }}>
            <div style={{ fontSize: 48 }}>🎬</div>
            <div style={{ color: "#fbbf24", fontWeight: 700, fontSize: 16 }}>Recording Not Available</div>
            <div style={{
              color: "rgba(255,255,255,0.45)", fontSize: 13, maxWidth: 480, lineHeight: 1.6,
              background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)",
              borderRadius: 10, padding: "14px 20px",
            }}>
              {error}
            </div>
            <button onClick={onClose} style={{
              background: "linear-gradient(135deg,#e63946,#c1121f)",
              color: "#fff", border: "none", borderRadius: 9,
              padding: "9px 28px", fontWeight: 700, fontSize: 13, cursor: "pointer",
              marginTop: 8,
            }}>Close</button>
          </div>
        ) : (
          <VideoPlayer url={url} />
        )}
      </div>
    </div>
  );
}

// ── Per-card recording button ──────────────────────────────────────────────────
function RecordingButton({ sessionId, sessionTitle, onOpen }) {
  const [loading, setLoading] = useState(false);

  async function load(e) {
    e.stopPropagation();
    setLoading(true);
    try {
      const res = await api.mockSessionRecordingUrl(sessionId);
      onOpen({ url: res.url, error: null });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Recording unavailable";
      onOpen({ url: null, error: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <button onClick={load} disabled={loading} style={{
      background: "rgba(96,165,250,0.1)",
      border: "1px solid rgba(96,165,250,0.35)",
      color: "#60a5fa",
      borderRadius: 8, padding: "6px 14px",
      fontSize: 12, fontWeight: 700, cursor: loading ? "wait" : "pointer",
      whiteSpace: "nowrap", transition: "all 0.2s",
      display: "flex", alignItems: "center", gap: 6,
      opacity: loading ? 0.7 : 1,
    }}>
      {loading
        ? <><span style={{ animation: "pulse-glow 0.8s ease-in-out infinite" }}>●</span> Loading…</>
        : <>▶ Watch Recording</>
      }
    </button>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function MockHistory() {
  const navigate = useNavigate();
  const [sessions, setSessions]   = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [hovered,  setHovered]    = useState(null);
  const [recModal, setRecModal]   = useState(null); // { url, title }

  // Duplicate mosaic for seamless loop
  const mosaic = [...MOSAIC_AVATARS, ...MOSAIC_AVATARS, ...MOSAIC_AVATARS];

  useEffect(() => {
    api.mockMySessions()
      .then((d) => setSessions(d.sessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{
      minHeight: "100vh", background: "#060a1f", color: "#f1f1f1",
      fontFamily: "'Inter',sans-serif", overflowX: "hidden", position: "relative",
    }}>
      <style>{`
        @keyframes cl-mosaic  { 0%{transform:translateX(0)} 100%{transform:translateX(-33.33%)} }
        @keyframes cl-scan    { 0%{top:-4px;opacity:0.6} 80%{opacity:0.6} 100%{top:100%;opacity:0} }
        @keyframes cl-particle{ 0%{transform:translateY(0) translateX(0) scale(1);opacity:0.8} 100%{transform:translateY(-80px) translateX(var(--dx,10px)) scale(0);opacity:0} }
        @keyframes cl-blink   { 0%,100%{opacity:1} 50%{opacity:0.2} }
        @keyframes cl-soundbar{ from{height:4px} to{height:22px} }
        @keyframes card-in    { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes pulse-glow { 0%,100%{opacity:0.5} 50%{opacity:1} }
      `}</style>

      {/* ── Cinematic background: horizontal scrolling avatar mosaic ── */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 0, overflow: "hidden", pointerEvents: "none",
      }}>
        {/* Mosaic row */}
        <div style={{
          position: "absolute", top: 0, bottom: 0,
          display: "flex", gap: 10, alignItems: "center",
          width: "max-content",
          animation: "cl-mosaic 60s linear infinite",
        }}>
          {mosaic.map((src, i) => (
            <img key={i} src={src} alt="" style={{
              height: "100vh", width: "auto", objectFit: "cover",
              flexShrink: 0,
              filter: "grayscale(60%) brightness(0.55)",
            }} />
          ))}
        </div>

        {/* Dark base overlay */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(6,10,31,0.82)" }} />

        {/* Red scan line sweeping across */}
        <div style={{
          position: "absolute", left: 0, right: 0, height: 2,
          background: "linear-gradient(90deg, transparent 0%, rgba(230,57,70,0.7) 40%, rgba(230,57,70,0.9) 50%, rgba(230,57,70,0.7) 60%, transparent 100%)",
          animation: "cl-scan 4s linear infinite",
          pointerEvents: "none",
        }} />

        {/* Scanline texture */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "repeating-linear-gradient(0deg, rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 3px)",
          backgroundSize: "100% 3px",
        }} />

        {/* Radial vignette */}
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at 50% 50%, transparent 30%, rgba(6,10,31,0.75) 100%)",
        }} />

        {/* Bottom red glow bar */}
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0, height: 120,
          background: "linear-gradient(to top, rgba(230,57,70,0.08), transparent)",
        }} />

        {/* Floating red particles */}
        {[
          { left: "8%",  top: "60%", delay: "0s",   dx: "12px"  },
          { left: "22%", top: "40%", delay: "1.1s", dx: "-8px"  },
          { left: "45%", top: "75%", delay: "2.3s", dx: "15px"  },
          { left: "67%", top: "35%", delay: "0.7s", dx: "-12px" },
          { left: "85%", top: "65%", delay: "1.8s", dx: "8px"   },
          { left: "55%", top: "50%", delay: "3.1s", dx: "-10px" },
        ].map((p, i) => (
          <div key={i} style={{
            position: "absolute", left: p.left, top: p.top,
            width: 5, height: 5, borderRadius: "50%",
            background: "#e63946", boxShadow: "0 0 8px rgba(230,57,70,0.9)",
            "--dx": p.dx,
            animation: `cl-particle 2.8s ease-out ${p.delay} infinite`,
          }} />
        ))}
      </div>

      {/* ── Header ── */}
      <div style={{ position: "relative", zIndex: 10 }}>
        <Header />
      </div>

      {/* ── Recording modal ── */}
      {recModal && (
        <RecordingModal
          url={recModal.url}
          error={recModal.error}
          sessionTitle={recModal.title}
          onClose={() => setRecModal(null)}
        />
      )}

      {/* ── Page content ── */}
      <div style={{ position: "relative", zIndex: 10, maxWidth: 880, margin: "0 auto", padding: "1.5rem 1.5rem 4rem" }}>

        {/* Title */}
        <div style={{ textAlign: "center", marginBottom: "2rem", animation: "card-in 0.5s ease-out" }}>
          {/* Sound bars decoration */}
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 3, height: 24, marginBottom: 12 }}>
            {[12, 20, 14, 26, 10, 22, 16, 24, 11, 19].map((h, i) => (
              <div key={i} style={{
                width: 3, borderRadius: 2, background: "#e63946", opacity: 0.65,
                animation: `cl-soundbar 0.45s ease-in-out ${(i * 0.06).toFixed(2)}s infinite alternate`,
                minHeight: 3,
              }} />
            ))}
          </div>
          {/* Status dot */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#e63946", animation: "cl-blink 1.2s ease-in-out infinite" }} />
            <span style={{ color: "rgba(230,57,70,0.8)", fontSize: 10, fontWeight: 700, letterSpacing: "0.28em", fontFamily: "monospace", textTransform: "uppercase" }}>
              Your Performance
            </span>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#e63946", animation: "cl-blink 1.2s ease-in-out 0.6s infinite" }} />
          </div>
          <h1 style={{
            fontFamily: "'Barlow Condensed','Impact',sans-serif",
            fontSize: "clamp(2.2rem,5vw,3.5rem)",
            fontWeight: 900, letterSpacing: "0.05em", textTransform: "uppercase",
            color: "#fff", lineHeight: 1, margin: 0,
            textShadow: "0 0 40px rgba(230,57,70,0.4)",
          }}>
            INTERVIEW <span style={{ color: "#e63946" }}>HISTORY</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 12.5, marginTop: 8, fontFamily: "monospace", letterSpacing: "0.08em" }}>
            TRACK YOUR PROGRESS ACROSS ALL MOCK SESSIONS
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "4rem" }}>
            <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#e63946", animation: `pulse-glow 1.2s ease-in-out ${i * 0.2}s infinite` }} />
              ))}
            </div>
            <div style={{ color: "rgba(255,255,255,0.3)", fontSize: 13, marginTop: 12, fontFamily: "monospace" }}>LOADING SESSIONS…</div>
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "4rem", animation: "card-in 0.5s ease-out" }}>
            <div style={{
              display: "inline-block",
              background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 20, padding: "3rem 4rem",
              backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
              boxShadow: "0 0 40px rgba(230,57,70,0.08)",
            }}>
              <div style={{ fontSize: 52, marginBottom: 14 }}>🎯</div>
              <div style={{ color: "#f1f1f1", fontWeight: 700, fontSize: 18, marginBottom: 6 }}>No sessions yet</div>
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginBottom: 24 }}>
                Jump into your first mock interview and start tracking your growth
              </div>
              <button onClick={() => navigate("/mock/positions")} style={{
                background: "linear-gradient(135deg,#e63946,#c1121f)",
                color: "#fff", border: "none", borderRadius: 10,
                padding: "11px 28px", fontWeight: 700, fontSize: 14, cursor: "pointer",
                boxShadow: "0 4px 20px rgba(230,57,70,0.4)",
              }}>Start Your First Mock →</button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {sessions.map((s, idx) => {
              const vs   = VERDICT[s.verdict] || VERDICT.pending;
              const dur  = Math.round((s.duration_seconds || 0) / 60);
              const done = s.stage === "completed";
              const isHov = hovered === s.id;
              const hasRec = !!s.recording_s3_key;

              return (
                <div key={s.id}
                  onMouseEnter={() => setHovered(s.id)}
                  onMouseLeave={() => setHovered(null)}
                  style={{
                    position: "relative", overflow: "hidden",
                    background: isHov ? "rgba(15,20,50,0.94)" : "rgba(8,12,35,0.82)",
                    border: `1px solid ${isHov ? vs.border : "rgba(255,255,255,0.07)"}`,
                    borderRadius: 14,
                    backdropFilter: "blur(22px)", WebkitBackdropFilter: "blur(22px)",
                    boxShadow: isHov ? `0 8px 40px rgba(0,0,0,0.5), 0 0 28px ${vs.glow}` : "0 2px 20px rgba(0,0,0,0.3)",
                    transition: "all 0.22s ease",
                    animation: `card-in 0.4s ease-out ${idx * 0.07}s both`,
                  }}
                >
                  {/* Red scan sweep on hover */}
                  {isHov && (
                    <div style={{
                      position: "absolute", left: 0, right: 0, height: 2, zIndex: 1,
                      background: "linear-gradient(90deg, transparent, rgba(230,57,70,0.6), transparent)",
                      animation: "cl-scan 2s linear infinite",
                      pointerEvents: "none",
                    }} />
                  )}

                  {/* Coloured left accent bar */}
                  <div style={{
                    position: "absolute", left: 0, top: 0, bottom: 0, width: 3,
                    background: `linear-gradient(to bottom, ${vs.color}, transparent)`,
                    borderRadius: "14px 0 0 14px",
                    opacity: isHov ? 1 : 0.5,
                    transition: "opacity 0.2s",
                  }} />

                  {/* Main card row */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
                    padding: "1rem 1.3rem",
                  }}>
                    {/* Score ring */}
                    {s.total_score != null && (
                      <ScoreRing score={s.total_score} max={s.total_max || 20} />
                    )}

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{
                        fontWeight: 800, fontSize: 14.5, color: "#f1f1f1", marginBottom: 4,
                        fontFamily: "'Barlow Condensed','Impact',sans-serif",
                        letterSpacing: "0.04em", textTransform: "uppercase",
                      }}>
                        {posLabel(s.position_id)}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "5px 14px" }}>
                        <span style={{ color: "rgba(255,255,255,0.36)", fontSize: 11.5 }}>📅 {fmt(s.started_at)}</span>
                        {dur > 0 && <span style={{ color: "rgba(255,255,255,0.36)", fontSize: 11.5 }}>⏱ {dur} min</span>}
                        <span style={{ color: "rgba(255,255,255,0.36)", fontSize: 11.5 }}>
                          🎙 {(s.transcript?.length || 0)} messages
                        </span>
                        {hasRec && (
                          <span style={{ color: "rgba(96,165,250,0.6)", fontSize: 11, fontFamily: "monospace" }}>● REC</span>
                        )}
                      </div>
                    </div>

                    {/* Verdict badge */}
                    <div style={{
                      background: vs.bg, border: `1px solid ${vs.border}`,
                      borderRadius: 20, padding: "4px 13px",
                      fontSize: 12, color: vs.color, fontWeight: 700,
                      flexShrink: 0, whiteSpace: "nowrap",
                      boxShadow: isHov ? `0 0 12px ${vs.glow}` : "none",
                      transition: "box-shadow 0.2s",
                    }}>
                      {vs.label}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      {done ? (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/mock/feedback/${s.id}`); }} style={{
                          background: isHov ? "linear-gradient(135deg,#e63946,#c1121f)" : "rgba(230,57,70,0.12)",
                          border: "1px solid rgba(230,57,70,0.4)",
                          color: "#fff", borderRadius: 8, padding: "6px 13px",
                          fontSize: 12, fontWeight: 700, cursor: "pointer",
                          transition: "all 0.2s",
                          boxShadow: isHov ? "0 4px 16px rgba(230,57,70,0.4)" : "none",
                          whiteSpace: "nowrap",
                        }}>
                          View Feedback →
                        </button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); navigate(`/mock/session/${s.id}`); }} style={{
                          background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.35)",
                          color: "#fbbf24", borderRadius: 8, padding: "6px 13px",
                          fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                        }}>
                          Resume →
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Recording button — opens modal */}
                    {hasRec && (
                    <div style={{ padding: "0.5rem 1.3rem 0.9rem", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <RecordingButton
                        sessionId={s.id}
                        sessionTitle={posLabel(s.position_id)}
                        onOpen={({ url, error }) => setRecModal({ url, error, title: posLabel(s.position_id) })}
                      />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Footer CTA */}
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button onClick={() => navigate("/mock/positions")} style={{
                background: "linear-gradient(135deg,#e63946,#c1121f)",
                color: "#fff", border: "none", borderRadius: 10,
                padding: "11px 32px", fontWeight: 700, fontSize: 14, cursor: "pointer",
                boxShadow: "0 4px 24px rgba(230,57,70,0.4)",
                letterSpacing: "0.04em",
              }}>
                + Start New Mock Interview
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
